<template>
    <article class="history-mission-card card border-0" :class="mission?.status === 'CANCELED'
        ? 'history-mission-card-canceled'
        : 'history-mission-card-finished'">
        <div class="history-mission-header">
            <div class="history-mission-title-wrap">
                <span class="history-status-badge" :class="mission?.status === 'CANCELED'
                    ? 'history-status-badge-canceled'
                    : 'history-status-badge-finished'">
                    {{ mission?.status === 'CANCELED' ? 'ANNULÉE' : 'TERMINÉE' }}
                </span>

                <div class="history-mission-main">
                    <router-link :to="'/mission/' + mission.id" class="history-mission-title">
                        {{ mission.name }}
                    </router-link>
                    <div class="history-mission-meta">
                        <span class="history-mission-id">#{{ mission.id }}</span>
                        <template v-if="mission.status !== 'CANCELED'">
                            <span class="history-meta-separator">•</span>
                            <span>Durée : {{ mission.duration }}</span>
                            <span class="history-meta-separator">•</span>
                            <span>Distance : {{ distanceDisplay }}</span>
                        </template>
                        <template v-if="mission.corridoring">
                            <span class="history-meta-separator">•</span>
                            <router-link :to="'/corridoringmap/' + mission.corridoring.id"
                                class="history-corridoring-link">
                                {{ mission.corridoring.name }}
                            </router-link>
                        </template>
                        <template v-else>
                            <span class="history-meta-separator">•</span>
                            <span class="text-muted fst-italic">Aucun corridoring</span>
                        </template>
                    </div>
                </div>
            </div>

            <div class="history-mission-actions">
                <router-link v-if="mission.status !== 'CANCELED'" :to="'/missionhistorymap/' + mission.id"
                    class="history-action-icon" title="Voir la carte d'historique">
                    <font-awesome-icon icon="location-dot" class="history-action-icon-svg" />
                </router-link>
                <span v-if="mission.status !== 'CANCELED'" class="history-action-icon"
                    title="Télécharger le rapport" @click="downloadMissionReport(mission.id)">
                    <font-awesome-icon icon="download" class="history-action-icon-svg" />
                </span>
            </div>
        </div>

        <div class="history-mission-grid">
            <div class="history-info-block">
                <div class="history-info-label">Début</div>
                <template v-if="mission.startDate">
                    <div class="history-info-date">{{ mission.displayStartDate }}</div>
                    <div class="history-info-address">{{ mission.startAddress }}</div>
                    <LatLongDisplay :longitude="mission.startLongitude" :latitude="mission.startLatitude" />
                </template>
                <template v-else>
                    <div class="history-info-date fst-italic">Non commencée</div>
                    <div class="history-info-address fst-italic">—</div>
                    <div class="fst-italic">—</div>
                </template>
            </div>

            <div class="history-info-block">
                <div class="history-info-label">Fin</div>
                <template v-if="mission.endDate">
                    <div class="history-info-date">{{ mission.displayEndDate }}</div>
                    <div class="history-info-address">{{ mission.endAddress }}</div>
                    <LatLongDisplay :longitude="mission.endLongitude" :latitude="mission.endLatitude" />
                </template>
                <template v-else>
                    <div class="history-info-date fst-italic">Non terminée</div>
                    <div class="history-info-address fst-italic">—</div>
                    <div class="fst-italic">—</div>
                </template>
            </div>


            <div class="history-info-block history-info-block-vehicles">
                <div class="history-info-label">Véhicules</div>
                <div class="history-vehicles-list">
                    <div v-if="mission.transporteur" class="history-vehicle-item">
                        <VehicleNameDisplay :balise="mission.transporteur" compact
                            class="history-vehicle-chip history-vehicle-chip-main" />
                    </div>
                    <div v-for="balise in mission.balises" :key="balise.id" class="history-vehicle-item">
                        <VehicleNameDisplay :balise="balise" compact
                            class="history-vehicle-chip history-vehicle-chip-secondary" />
                    </div>
                </div>
            </div>
        </div>
    </article>
</template>

<script>
import VehicleNameDisplay from '@/components/display/VehicleNameDisplay.vue'
import LatLongDisplay from '@/components/display/LatLongDisplay.vue'
import ReportService from '@/services/report.service.js'

export default {
    props: ['mission'],
    components: {
        VehicleNameDisplay,
        LatLongDisplay,
    },
    computed: {
        distanceDisplay() {
            if (!this.mission?.distance) return '—';
            return this.mission?.distance + ' km';
        }
    },
    methods: {
        async downloadMissionReport(missionId) {
            try {
                const resp = await ReportService.getMissionReport(missionId);
                const { downloadUrl } = resp.data || {};
                if (!downloadUrl) throw new Error('downloadUrl manquant');
                const apiBase = process.env.VUE_APP_API_URL;
                let origin;
                try { origin = new URL(apiBase).origin; }
                catch { origin = window.location.origin; }
                const absolute = new URL(downloadUrl, origin).toString();
                const a = document.createElement('a');
                a.href = absolute;
                a.target = '_blank';
                a.rel = 'noopener';
                document.body.appendChild(a);
                a.click();
                a.remove();
            } catch (e) {
                console.error('Erreur lors du téléchargement du rapport mission :', e);
                alert('Impossible de télécharger le rapport de mission.');
            }
        }
    }
}
</script>

<style scoped>
.history-mission-card {
    position: relative;
    border-radius: 18px;
    background: #ffffff;
    border: 1px solid #bcc9d8;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.09), 0 1px 3px rgba(15, 23, 42, 0.05) !important;
    overflow: hidden;
}

.history-mission-card::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
}

.history-mission-card-finished::before {
    background: #3b82f6;
    /* bleu neutre */
}

.history-mission-card-canceled::before {
    background: #dc3545;
    /* rouge */
}

.history-mission-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.85rem;
    padding: 0.9rem 1.1rem 0.8rem;
    border-bottom: 1px solid #e8eef5;
}

.history-mission-title-wrap {
    display: flex;
    align-items: center;
    gap: 0.9rem;
    min-width: 0;
}

.history-status-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 32px;
    padding: 0.35rem 0.85rem;
    border-radius: 999px;
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    white-space: nowrap;
}

.history-status-badge-finished {
    background: rgba(59, 130, 246, 0.12);
    color: #3b82f6;
}

.history-status-badge-canceled {
    background: rgba(220, 53, 69, 0.12);
    color: #b02a37;
}

.history-mission-main {
    min-width: 0;
}

.history-mission-title {
    display: inline-block;
    color: #0f172a;
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.35;
    text-decoration: none;
    word-break: break-word;
}

.history-mission-title:hover {
    color: #0d6efd;
}

.history-mission-meta {
    margin-top: 0.3rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    color: #64748b;
    font-size: 0.88rem;
}

.history-mission-id {
    font-weight: 700;
}

.history-meta-separator {
    color: #94a3b8;
}

.history-mission-actions {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    flex-shrink: 0;
}

.history-action-icon {
    min-width: 34px;
    height: 34px;
    padding: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    color: #0d6efd;
    background: rgba(13, 110, 253, 0.08);
    text-decoration: none;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease;
    flex-shrink: 0;
    line-height: 1;
}

.history-action-icon svg {
    width: 22px;
    height: 22px;
    display: block;
    flex-shrink: 0;
}

.history-action-icon:hover {
    color: #0b5ed7;
    background: rgba(13, 110, 253, 0.14);
}

.history-mission-grid {
    display: grid;
    grid-template-columns: minmax(220px, 1fr) minmax(220px, 1fr) minmax(280px, 1.3fr);
    gap: 1rem 1.25rem;
    padding: 1rem 1.1rem;
}

.history-info-block {
    min-width: 0;
}

.history-info-label {
    margin-bottom: 0.35rem;
    font-size: 0.76rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #64748b;
}

.history-info-date {
    margin-bottom: 0.3rem;
    color: #475569;
    font-size: 0.92rem;
}

.history-info-address {
    margin-bottom: 0.35rem;
    color: #0f172a;
    line-height: 1.45;
    word-break: break-word;
}

.history-corridoring-link {
    color: #0d6efd;
    text-decoration: none;
    word-break: break-word;
}

.history-corridoring-link:hover {
    text-decoration: underline;
}

.history-info-block-vehicles {
    min-width: 0;
}

.history-vehicles-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
    min-width: 0;
    align-items: stretch;
}

.history-vehicle-item {
    display: flex;
    align-items: stretch;
}

@media (max-width: 991.98px) {
    .history-mission-grid {
        grid-template-columns: 1fr 1fr;
    }

    .history-info-block-vehicles {
        grid-column: 1 / -1;
    }
}

@media (max-width: 767.98px) {
    .history-mission-card {
        border-radius: 16px;
    }

    .history-mission-header {
        flex-direction: column;
        align-items: stretch;
    }

    .history-mission-title-wrap {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.65rem;
    }

    .history-mission-actions {
        justify-content: flex-end;
    }

    .history-mission-grid {
        grid-template-columns: 1fr;
    }
}

.history-vehicle-chip {
    border-radius: 10px !important;
    padding: 0.1rem 0.45rem !important;
}

.history-vehicle-chip-main {
    background: rgba(25, 135, 84, 0.12);
}

.history-vehicle-chip-secondary {
    background: rgba(13, 110, 253, 0.1);
}
</style>